import { Component, OnInit } from '@angular/core';
import { ROUTES } from 'src/app/constants/Routes';


@Component({
  selector: 'app-oasis',
  templateUrl: './oasis.component.html',
  styleUrls: ['./oasis.component.scss'],
  standalone: false
})
export class OasisComponent implements OnInit {
  Routes = ROUTES;
  activeSection = 'overview';

  sections = [
    { id: 'overview', label: 'Overview', icon: 'bx-layer' },
    { id: 'capabilities', label: 'Capabilities', icon: 'bx-cog' },
    { id: 'usecases', label: 'Use Cases', icon: 'bx-bulb' },
    { id: 'certificate', label: 'Certificate Layer', icon: 'bx-shield-quarter' },
    { id: 'workflow', label: 'Workflow', icon: 'bx-git-branch' },
  ];

  workflowImage = 'assets/images/websitev2/workflow-diagrams/oasis.png';

  overviewHighlights = [
    { val: 'FOTA + COTA', label: 'One control plane for firmware, software, map, media, and infotainment content updates.' },
    { val: 'Phased Rollouts', label: 'Execute targeted or fleet-wide OTA campaigns with controlled segmentation and delivery visibility.' },
    { val: 'Trusted Delivery', label: 'Secure packages, identities, communication, and in-vehicle execution across the OTA lifecycle.' },
  ];

  capabilities = [
    {
      icon: 'bx-package',
      title: 'Centralized Content Management',
      desc: 'Upload and manage FOTA packages (firmware, OS updates, security patches for critical ECUs) and COTA packages (maps, applications, media, UI updates for infotainment). Structured packaging, version control, and rollout planning included.',
    },
    {
      icon: 'bx-cloud',
      title: 'Cloud-Based OTA Orchestration',
      desc: 'Manage OTA campaigns across vehicle fleets — configure single or bulk deployments, create targeted groups based on vehicle type, region, or usage, and schedule controlled phased rollouts.',
    },
    {
      icon: 'bx-shield-alt-2',
      title: 'Secure Wireless Update Delivery',
      desc: 'Supports secure OTA over cellular networks and Wi-Fi with MQTT/DBC integration. Ensures encrypted communication, reliable delivery, and full update integrity and validation.',
    },
    {
      icon: 'bx-car',
      title: 'Vehicle-Level Integration',
      desc: 'Connects with the Vehicle OTA Gateway / Telematics Unit for seamless communication with in-vehicle systems, controlled update execution, and real-time status reporting.',
    },
    {
      icon: 'bx-cog',
      title: 'Critical ECU Update Management (FOTA)',
      desc: 'Manage updates for safety-critical systems including Engine, Brakes, ADAS, and Battery — with controlled deployment strategies, failure handling & rollback mechanisms, and safety-first update sequencing.',
    },
    {
      icon: 'bx-bar-chart-alt-2',
      title: 'Real-Time Monitoring & Insights',
      desc: 'Campaign-level tracking, device-level update status (Success / Failure / Pending), live progress monitoring, logs and diagnostics — giving OEMs complete visibility and control.',
    },
  ];

  usecases = [
    {
      icon: 'bx-mobile-alt',
      tag: 'COTA',
      title: 'Infotainment and UX Updates',
      desc: 'Refresh infotainment head units, maps, applications, and user interfaces without service-center visits or vehicle downtime.',
    },
    {
      icon: 'bx-shield',
      tag: 'Security',
      title: 'Security Patch Management',
      desc: 'Distribute urgent patches quickly, reduce exposure windows, and maintain cybersecurity compliance across connected fleets.',
    },
    {
      icon: 'bx-slider-alt',
      tag: 'Campaigns',
      title: 'Targeted Campaign Deployment',
      desc: 'Launch phased rollouts by model, geography, software version, or operational profile with precise deployment controls.',
    },
    {
      icon: 'bx-world',
      tag: 'Operations',
      title: 'Fleet-Wide Update Management',
      desc: 'Coordinate OTA programs across thousands of vehicles while keeping rollout visibility, consistency, and auditability centralized.',
    },
  ];

  certificateCapabilities = [
    {
      icon: 'bx-id-card',
      title: 'Device Identity Provisioning',
      desc: 'Provision trusted identities for TCUs, ECUs, and IoT endpoints using hardware-backed key storage and tamper-resistant onboarding.',
    },
    {
      icon: 'bx-refresh',
      title: 'Certificate Lifecycle Management',
      desc: 'Automate issuance, renewal, rotation, and revocation with traceable audit history and policy-driven governance.',
    },
    {
      icon: 'bx-transfer-alt',
      title: 'Mutual Authentication',
      desc: 'Enable verified communication between vehicles, cloud services, and apps so only trusted entities can exchange data.',
    },
    {
      icon: 'bx-check-shield',
      title: 'Secure OTA Trust Chain',
      desc: 'Protect update packages with cryptographic signing and validation to preserve integrity from cloud to vehicle.',
    },
  ];

  businessImpact = [
    'Supports zero-trust security architecture across connected ecosystems.',
    'Reduces cybersecurity risk at fleet and platform scale.',
    'Enables onboarding for millions of devices and partners.',
    'Improves compliance readiness across automotive and IoT programs.',
  ];

  ngOnInit() {}


  scrollTo(id: string) {
    this.activeSection = id;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
